import { Box, Chip } from '@mui/material'
import { humanizeServiceName } from '@common/helpers/atlasAI'
import { IApprovalSummary } from '@common/interfaces/atlasai'
import Checkbox from '../../Common/Checkbox'
import Text from '../../Common/Text'
import styles from './PendingApprovals.module.scss'

interface IProps {
  approvals: IApprovalSummary[]
  /**
   * Rows the last batch failed that have since left the queue — a failed approve
   * is retired server-side. Shown unticked and disabled, so the reason stays readable.
   */
  retiredApprovals: IApprovalSummary[]
  selectedIds: Set<string>
  /** Reason each failed approval could not be resolved, keyed by approval id. */
  failuresById: Record<string, string>
  disabled: boolean
  onToggle: (approvalId: string) => void
  onToggleAll: () => void
}

interface ISelectionRowProps {
  approval: IApprovalSummary
  isChecked: boolean
  isDisabled: boolean
  failure?: string
  onToggle: (approvalId: string) => void
}

const SelectionRow = ({
  approval,
  isChecked,
  isDisabled,
  failure,
  onToggle,
}: ISelectionRowProps) => (
  <Box className={styles.selectionRow}>
    <Checkbox
      size="small"
      checked={isChecked}
      disabled={isDisabled}
      onChange={() => onToggle(approval.id)}
      slotProps={{
        input: {
          'aria-label': `Select ${approval.preview?.title ?? approval.serviceName}`,
        },
      }}
    />
    <Box className={styles.selectionRowBody}>
      <Box className={styles.selectionRowHeader}>
        <Text variant="bodyLg" casing="none" className={styles.selectionRowTitle}>
          {approval.preview?.title ?? humanizeServiceName(approval.serviceName)}
        </Text>
        <Chip
          size="small"
          label={humanizeServiceName(approval.serviceName)}
          className={styles.serviceChip}
        />
      </Box>
      {approval.preview?.subtitle && (
        <Text variant="bodySm" tone="secondary" component="span" casing="none">
          {approval.preview.subtitle}
        </Text>
      )}
      {failure && (
        <Text variant="bodySm" tone="error" component="span" casing="none">
          {failure}
        </Text>
      )}
    </Box>
  </Box>
)

/**
 * The multi-select view of the pending approvals — the card carousel collapsed
 * to one tickable line each, so a run that raised thirty cards can be answered
 * once instead of thirty times.
 *
 * Deliberately read-only per row: a row carries enough to recognise the action
 * (its preview title and the service that queued it) but no inline editing.
 * "Accept with Edits" is a judgement about a single action, so it stays on the
 * full card.
 */
const ApprovalSelectionList = ({
  approvals,
  retiredApprovals,
  selectedIds,
  failuresById,
  disabled,
  onToggle,
  onToggleAll,
}: IProps) => {
  const selectedCount = approvals.filter((approval) => selectedIds.has(approval.id)).length
  const allSelected = approvals.length > 0 && selectedCount === approvals.length

  return (
    <Box className={styles.selectionList}>
      <Box className={styles.selectionHeader}>
        {/* Partial selection is conveyed by the "N selected" caption rather than
            an indeterminate glyph — the shared Checkbox has no tokenised mixed
            icon, and MUI's default one is frozen at a light-mode colour. */}
        <Checkbox
          size="small"
          checked={allSelected}
          disabled={disabled || approvals.length === 0}
          onChange={onToggleAll}
          slotProps={{ input: { 'aria-label': 'Select all approvals' } }}
        />
        <Text variant="bodySm" tone="secondary" strong component="span">
          {selectedCount > 0 ? `${selectedCount} selected` : 'Select all'}
        </Text>
      </Box>

      <Box className={styles.selectionRows}>
        {approvals.map((approval) => (
          <SelectionRow
            key={approval.id}
            approval={approval}
            isChecked={selectedIds.has(approval.id)}
            isDisabled={disabled}
            failure={failuresById[approval.id]}
            onToggle={onToggle}
          />
        ))}
        {retiredApprovals.map((approval) => (
          <SelectionRow
            key={approval.id}
            approval={approval}
            isChecked={false}
            isDisabled
            failure={failuresById[approval.id]}
            onToggle={onToggle}
          />
        ))}
      </Box>
    </Box>
  )
}

export default ApprovalSelectionList
